/**
 * Unified Utility Functions
 * Common formatting, validation, and data manipulation functions
 */

export const matchesPlantScope = (value, selectedPlant) => {
  if (!selectedPlant || ['all', 'all units'].includes(String(selectedPlant).trim().toLowerCase())) return true;
  let selectedNames = [String(selectedPlant).trim().toLowerCase()];
  try {
    const plants = JSON.parse(localStorage.getItem('availablePlants') || '[]');
    const selected = plants.find((plant) => String(plant.plant_code).trim().toLowerCase() === selectedNames[0]);
    if (selected?.plant_name) selectedNames.push(String(selected.plant_name).trim().toLowerCase());
  } catch (error) {}
  return String(value || '').split(/[|,;]/).some((unit) => selectedNames.includes(unit.trim().toLowerCase()));
};

// Date Formatting
export const formatDate = (date, format = 'short') => {
  if (!date) return '';
  const d = new Date(date);
  if (format === 'short') {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  if (format === 'long') {
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }
  if (format === 'time') {
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
  if (format === 'datetime') {
    return `${formatDate(d, 'short')} ${formatDate(d, 'time')}`;
  }
  return d.toISOString();
};

export const formatTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const now = new Date();
  const diff = now - d;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return `${seconds}s ago`;
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  
  return formatDate(d, 'short');
};

// Number Formatting
export const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined) return '0';
  return parseFloat(num).toFixed(decimals);
};

export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const formatPercent = (value, decimals = 1) => {
  return `${formatNumber(value * 100, decimals)}%`;
};

// String Formatting
export const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const toTitleCase = (str) => {
  if (!str) return '';
  return str.split(' ').map(word => capitalizeFirst(word.toLowerCase())).join(' ');
};

export const truncate = (str, length = 50, suffix = '...') => {
  if (!str || str.length <= length) return str;
  return str.substring(0, length) + suffix;
};

export const getInitials = (name) => {
  if (!name) return '';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Validation
export const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const isValidPhone = (phone) => {
  const re = /^[0-9\-\+\(\)\s]{10,}$/;
  return re.test(phone);
};

export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const validateRequired = (value) => {
  return value && value.toString().trim() !== '';
};

export const validateMinLength = (value, min) => {
  return value && value.toString().length >= min;
};

export const validateMaxLength = (value, max) => {
  return !value || value.toString().length <= max;
};

export const validateRange = (value, min, max) => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= min && num <= max;
};

// Array Operations
export const removeDuplicates = (arr, key) => {
  if (!key) return [...new Set(arr)];
  return arr.filter((item, index, self) =>
    index === self.findIndex(t => t[key] === item[key])
  );
};

export const groupBy = (arr, key) => {
  return arr.reduce((groups, item) => {
    const groupKey = item[key];
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(item);
    return groups;
  }, {});
};

export const sortBy = (arr, key, direction = 'asc') => {
  const sorted = [...arr].sort((a, b) => {
    if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
    if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
    return 0;
  });
  return sorted;
};

export const searchArray = (arr, searchTerm, fields) => {
  if (!searchTerm) return arr;
  const term = searchTerm.toLowerCase();
  return arr.filter(item =>
    fields.some(field =>
      item[field]?.toString().toLowerCase().includes(term)
    )
  );
};

// Object Operations
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

export const mergeObjects = (target, ...sources) => {
  return Object.assign({}, target, ...sources);
};

export const pick = (obj, keys) => {
  const result = {};
  keys.forEach(key => {
    if (key in obj) result[key] = obj[key];
  });
  return result;
};

export const omit = (obj, keys) => {
  const result = { ...obj };
  keys.forEach(key => delete result[key]);
  return result;
};

// Status Color Mapping
export const getStatusColor = (status) => {
  const statusMap = {
    completed: 'success',
    pending: 'warning',
    failed: 'danger',
    approved: 'success',
    rejected: 'danger',
    active: 'success',
    inactive: 'secondary',
    processing: 'info',
    error: 'danger',
  };
  return statusMap[status?.toLowerCase()] || 'secondary';
};

export const getStatusBadgeClass = (status) => {
  return `badge-${getStatusColor(status)}`;
};

// Local Storage Operations
export const setStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error setting localStorage:', error);
  }
};

export const getStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error getting localStorage:', error);
    return defaultValue;
  }
};

export const removeStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing localStorage:', error);
  }
};

// Debounce and Throttle
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// CSV Export
export const exportToCSV = (data, filename = 'export.csv') => {
  if (!data || data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csv = [
    headers.join(','),
    ...data.map(row =>
      headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
};

// Notification Center
export const showNotification = (toast, message, type = 'info', duration = 3000) => {
  toast[type]?.(message, { autoClose: duration });
};

// Form Data to Object
export const formDataToObject = (formData) => {
  const obj = {};
  formData.forEach((value, key) => {
    if (obj[key]) {
      if (!Array.isArray(obj[key])) {
        obj[key] = [obj[key]];
      }
      obj[key].push(value);
    } else {
      obj[key] = value;
    }
  });
  return obj;
};
